/**
 * Discount Display Module
 * Automatically displays discounts from Shopify Discounts section on product cards and product pages
 * 
 * This script checks for discounts by temporarily adding items to cart and checking the final price.
 * Note: This requires the discount to be set up in Shopify Discounts section and applied to collections/products.
 */

class DiscountDisplay {
  constructor() {
    this.discountCache = new Map();
    // Configure discount codes to automatically apply
    // IMPORTANT: Discount codes require manual entry at checkout
    // For automatic discounts, use "Automatic discount" in Shopify instead
    // Add your discount codes here if you want to try applying them automatically
    this.discountCodes = {
      // 'ZFQ3H0T737WA': 'Body Care', // Example - uncomment and add your codes
    };
    this.init();
  }

  init() {
    // Don't run in theme editor or if cart API is not available
    if (window.Shopify?.designMode || document.querySelector('iframe[name*="theme-editor"]')) {
      return;
    }

    // Check if we should run - only if cart is empty or very small
    this.shouldRun().then((canRun) => {
      if (!canRun) {
        return; // Don't run if cart has items
      }

      // Wait for DOM to be ready
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => {
          // Additional delay for dynamic content
          setTimeout(() => this.updateDiscounts(), 1000);
        });
      } else {
        // Small delay to ensure page is fully loaded, especially for dynamic content
        setTimeout(() => this.updateDiscounts(), 1000);
      }
      
      // Also listen for cart updates that might trigger new product cards
      document.addEventListener('cart:updated', () => {
        setTimeout(() => this.updateDiscounts(), 500);
      });

      // Update discounts when variant changes on product page
      document.addEventListener('variant:change', () => {
        setTimeout(() => this.updateDiscounts(), 200);
      });
    }).catch(() => {
      // Silently fail if cart check fails
    });
  }

  /**
   * Check if we should run discount checks
   * Now we can use items from cart if available, or add temporarily if empty
   */
  async shouldRun() {
    try {
      const cartResponse = await fetch('/cart.js');
      if (!cartResponse.ok) {
        console.warn('DiscountDisplay: Cannot check cart, skipping');
        return false;
      }
      const cart = await cartResponse.json();
      // Always run - we can use existing cart items or add temporarily
      console.log(`DiscountDisplay: Cart has ${cart.items.length} items, will use for discount checks`);
      return true;
    } catch (error) {
      console.warn('DiscountDisplay: Error checking cart:', error);
      return false;
    }
  }

  /**
   * Update discount prices on all product cards and product pages
   */
  async updateDiscounts() {
    // Small delay to ensure DOM is fully rendered
    await new Promise(resolve => setTimeout(resolve, 100));

    // Find all price elements - they contain product information
    const priceElements = document.querySelectorAll('.price:not(.discount-applied)');
    
    if (priceElements.length > 0) {
      console.log(`DiscountDisplay: Found ${priceElements.length} price elements to process`);
      
      // Find parent cards for each price element
      const cardsWithPrice = [];
      priceElements.forEach(priceEl => {
        // Always find the card-wrapper first (top level container)
        let card = priceEl.closest('.card-wrapper, .product-card-wrapper');
        
        // If not found, try to find card element
        if (!card) {
          card = priceEl.closest('.card');
        }
        
        // If still not found, find the closest element with card classes
        if (!card) {
          let parent = priceEl.parentElement;
          let depth = 0;
          while (parent && depth < 10) {
            if (parent.classList && (
              parent.classList.contains('card-wrapper') ||
              parent.classList.contains('product-card-wrapper') ||
              parent.classList.contains('card')
            )) {
              card = parent;
              break;
            }
            parent = parent.parentElement;
            depth++;
          }
        }
        
        if (card && !cardsWithPrice.includes(card)) {
          cardsWithPrice.push(card);
        } else if (!card) {
          // If no card found, use the price element itself as a target
          cardsWithPrice.push(priceEl);
        }
      });
      
      if (cardsWithPrice.length > 0) {
        console.log(`DiscountDisplay: Found ${cardsWithPrice.length} product cards/price containers to process`);
        await this.updateProductCards(cardsWithPrice);
      }
    } else {
      console.warn('DiscountDisplay: No price elements found on page');
    }
    
    // Update product page
    const productPage = document.querySelector('product-info, [data-product-id]');
    if (productPage) {
      await this.updateProductPage(productPage);
    }
  }

  /**
   * Update discount prices on product cards
   */
  async updateProductCards(cards) {
    // Get current cart to use existing items if available
    let currentCart = { items: [] };
    try {
      const cartCheck = await fetch('/cart.js');
      if (cartCheck.ok) {
        currentCart = await cartCheck.json();
        console.log(`DiscountDisplay: Using cart with ${currentCart.items.length} items for discount checks`);
      }
    } catch (error) {
      console.warn('DiscountDisplay: Could not fetch cart, will only check items if cart is empty');
    }

    // Limit number of cards to process to avoid overwhelming the server
    const maxCards = 20; // Increased limit since we can use existing cart items
    const cardArray = Array.from(cards).slice(0, maxCards);
    
    // Process cards one at a time to minimize conflicts
    for (let i = 0; i < cardArray.length; i++) {
      const card = cardArray[i];
      const variantId = this.getVariantIdFromCard(card);
      
      if (!variantId) {
        console.warn('Variant ID not found for card:', card);
        continue;
      }

      try {
        // Add delay between requests
        if (i > 0) {
          await new Promise(resolve => setTimeout(resolve, 300));
        }
        
        // Check if variant is already in cart - if so, we can check it directly
        const cartItem = currentCart.items.find(item => item.variant_id === parseInt(variantId));
        if (cartItem) {
          // Check if product should be excluded from discount display
          // Check for data attribute or metafield indicator
          const productLink = card.querySelector('a[href*="/products/"]');
          const excludeFromDiscount = card.dataset.excludeFromDiscount === 'true' || 
                                     card.hasAttribute('data-exclude-from-discount');
          
          // If product is excluded, skip discount display
          if (excludeFromDiscount) {
            console.log(`Skipping discount for excluded product, variant ${variantId}`);
            continue;
          }
          
          // Use cart item directly - no need to add to cart
          // Check both original_price/final_price and line_price/final_line_price
          const priceDiscount = cartItem.original_price > cartItem.final_price;
          const lineDiscount = cartItem.line_price && cartItem.final_line_price && 
                              cartItem.line_price > cartItem.final_line_price;
          const hasDiscount = priceDiscount || lineDiscount;
          
          // Calculate price per item (not total line price)
          // If quantity > 1, divide line_price by quantity to get per-item price
          const quantity = cartItem.quantity || 1;
          let effectiveOriginalPrice = cartItem.original_price;
          let effectiveFinalPrice = cartItem.final_price;
          
          // If we have line prices and quantity > 1, calculate per-item price
          if (cartItem.line_price && cartItem.final_line_price && quantity > 1) {
            effectiveOriginalPrice = Math.round(cartItem.line_price / quantity);
            effectiveFinalPrice = Math.round(cartItem.final_line_price / quantity);
          } else if (cartItem.line_price && cartItem.final_line_price) {
            // Quantity is 1, use line prices directly (they're the same as item prices)
            effectiveOriginalPrice = cartItem.line_price;
            effectiveFinalPrice = cartItem.final_line_price;
          }
          
          const discountInfo = {
            hasDiscount,
            originalPrice: hasDiscount ? effectiveOriginalPrice : null,
            discountedPrice: hasDiscount ? effectiveFinalPrice : effectiveOriginalPrice,
            discountAmount: hasDiscount ? Math.max(0, effectiveOriginalPrice - effectiveFinalPrice) : 0,
            discountPercentage: hasDiscount 
              ? Math.round(((effectiveOriginalPrice - effectiveFinalPrice) / effectiveOriginalPrice) * 100) 
              : 0
          };
          
          if (discountInfo.hasDiscount) {
            console.log(`Applying discount to card ${i + 1} (from cart):`, discountInfo);
            this.applyDiscountToCard(card, discountInfo);
          }
          continue; // Skip to next card
        }
        
        // Variant not in cart - only check if cart is empty
        if (currentCart.items.length > 0) {
          // Skip items not in cart to avoid affecting user's cart
          continue;
        }
        
        // Check if product should be excluded from discount display
        const excludeFromDiscount = card.dataset.excludeFromDiscount === 'true' || 
                                   card.hasAttribute('data-exclude-from-discount');
        
        // If product is excluded, skip discount check
        if (excludeFromDiscount) {
          console.log(`Skipping discount check for excluded product, variant ${variantId}`);
          continue;
        }
        
        // Cart is empty, safe to check discount
        const discountInfo = await this.getDiscountForVariant(variantId);
        if (discountInfo && discountInfo.hasDiscount) {
          console.log(`Applying discount to card ${i + 1}:`, discountInfo);
          this.applyDiscountToCard(card, discountInfo);
        } else if (discountInfo) {
          console.log(`No discount for variant ${variantId}`);
        }
      } catch (error) {
        console.warn(`Error processing card ${i + 1}:`, error);
        // Continue to next card instead of breaking
      }
    }
  }

  /**
   * Update discount price on product page
   */
  async updateProductPage(productElement) {
    const variantId = this.getSelectedVariantId();
    if (!variantId) return;

    try {
      const discountInfo = await this.getDiscountForVariant(variantId);
      if (discountInfo && discountInfo.hasDiscount) {
        this.applyDiscountToProductPage(discountInfo);
      }
    } catch (error) {
      console.error('Error updating discount for product page:', error);
    }
  }

  /**
   * Get discount information for a variant by checking cart price
   */
  async getDiscountForVariant(variantId) {
    const cacheKey = variantId;
    
    // Check cache first (cache for 5 minutes)
    if (this.discountCache.has(cacheKey)) {
      const cached = this.discountCache.get(cacheKey);
      if (Date.now() - cached.timestamp < 300000) { // 5 minutes
        return cached.data;
      }
    }

    try {
      // Get current cart to preserve it
      const currentCartResponse = await fetch('/cart.js');
      if (!currentCartResponse.ok) {
        return null;
      }
      const currentCart = await currentCartResponse.json();
      
      // Check if variant is already in cart
      const existingItem = currentCart.items.find(item => item.variant_id === parseInt(variantId));
      
      let addedItem = null;
      let itemWasAdded = false;
      
      if (existingItem) {
        // Use existing item from cart - no need to add/remove
        console.log(`Using existing item from cart for variant ${variantId}`);
        addedItem = existingItem;
      } else {
        // If cart is not empty, we can't safely add items without affecting user's cart
        // So we'll skip checking discounts for items not in cart
        if (currentCart.items.length > 0) {
          console.log(`Variant ${variantId} not in cart, skipping discount check to avoid cart conflicts`);
          // Cache negative result
          this.discountCache.set(cacheKey, {
            data: { hasDiscount: false },
            timestamp: Date.now()
          });
          return null;
        }
        
        // Cart is empty, safe to add item temporarily
        try {
          // Note: Discount codes cannot be applied automatically via Cart API
          // Only automatic discounts (without codes) will be detected
          const addBody = { id: variantId, quantity: 1 };
          
          console.log(`Adding variant ${variantId} to empty cart to check for discounts...`);
          
          const addResponse = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(addBody)
          });

          // Handle 409 Conflict - immediately cache negative and return
          if (addResponse.status === 409) {
            // Cache negative result to avoid repeated attempts
            this.discountCache.set(cacheKey, {
              data: { hasDiscount: false },
              timestamp: Date.now()
            });
            return null;
          }
          
          if (!addResponse.ok) {
            // Cache negative result for other errors
            this.discountCache.set(cacheKey, {
              data: { hasDiscount: false },
              timestamp: Date.now()
            });
            return null;
          }
          
          itemWasAdded = true;
          // Get updated cart
          const updatedCartResponse = await fetch('/cart.js');
          if (!updatedCartResponse.ok) {
            // Try to restore cart
            await this.restoreCart(currentCart).catch(() => {});
            return null;
          }
          const updatedCart = await updatedCartResponse.json();
          
          // Find the item we just added
          addedItem = updatedCart.items.find(item => 
            item.variant_id === parseInt(variantId) && 
            !currentCart.items.some(cItem => cItem.key === item.key)
          );
          
          if (!addedItem) {
            // Item not found, restore cart
            await this.restoreCart(currentCart).catch(() => {});
            return null;
          }
          
          // Check if cart has discount applications
          if (updatedCart.discount_applications && updatedCart.discount_applications.length > 0) {
            console.log('Cart has discount applications:', updatedCart.discount_applications);
          }
        } catch (error) {
          // Silently fail - don't spam console
          // Try to restore cart if we added something
          if (itemWasAdded) {
            await this.restoreCart(currentCart).catch(() => {});
          }
          return null;
        }
      }
      
      if (!addedItem) {
        return null;
      }

      // Calculate discount
      const originalPrice = addedItem.original_price;
      const finalPrice = addedItem.final_price;
      const hasDiscount = originalPrice > finalPrice;
      
      // Also check line_price and final_line_price for discounts
      const linePriceDiscount = addedItem.line_price && addedItem.final_line_price && 
                                addedItem.line_price > addedItem.final_line_price;
      
      const finalHasDiscount = hasDiscount || linePriceDiscount;
      
      console.log(`Discount check for variant ${variantId}:`, {
        originalPrice,
        finalPrice,
        linePrice: addedItem.line_price,
        finalLinePrice: addedItem.final_line_price,
        hasDiscount: finalHasDiscount,
        discounts: addedItem.discounts || [],
        discountAllocations: addedItem.discount_allocations || []
      });
      
      // Remove the temporary item only if we added it
      if (itemWasAdded) {
        await this.restoreCart(currentCart);
      }
      
      // Calculate price per item (not total line price)
      // If quantity > 1, divide line_price by quantity to get per-item price
      const quantity = addedItem.quantity || 1;
      let effectiveOriginalPrice = originalPrice;
      let effectiveFinalPrice = finalPrice;
      
      // If we have line prices and quantity > 1, calculate per-item price
      if (addedItem.line_price && addedItem.final_line_price && quantity > 1) {
        effectiveOriginalPrice = Math.round(addedItem.line_price / quantity);
        effectiveFinalPrice = Math.round(addedItem.final_line_price / quantity);
      } else if (addedItem.line_price && addedItem.final_line_price) {
        // Quantity is 1, use line prices directly (they're the same as item prices)
        effectiveOriginalPrice = addedItem.line_price;
        effectiveFinalPrice = addedItem.final_line_price;
      }
      
      const effectiveHasDiscount = effectiveOriginalPrice > effectiveFinalPrice;
      
      const discountInfo = {
        hasDiscount: effectiveHasDiscount,
        originalPrice: effectiveHasDiscount ? effectiveOriginalPrice : null,
        discountedPrice: effectiveHasDiscount ? effectiveFinalPrice : effectiveOriginalPrice,
        discountAmount: effectiveHasDiscount ? (effectiveOriginalPrice - effectiveFinalPrice) : 0,
        discountPercentage: effectiveHasDiscount ? Math.round(((effectiveOriginalPrice - effectiveFinalPrice) / effectiveOriginalPrice) * 100) : 0
      };

      // Cache the result
      this.discountCache.set(cacheKey, {
        data: discountInfo,
        timestamp: Date.now()
      });
      
      return discountInfo;
    } catch (error) {
      // Silently fail - don't spam console with errors
      return null;
    }
  }

  /**
   * Restore cart to previous state
   */
  async restoreCart(previousCart) {
    try {
      // Clear cart
      await fetch('/cart/clear.js', { method: 'POST' });
      
      // Restore items
      if (previousCart.items.length > 0) {
        const items = previousCart.items.map(item => ({
          id: item.variant_id,
          quantity: item.quantity,
          properties: item.properties
        }));
        
        await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items })
        });
      }
    } catch (error) {
      console.error('Error restoring cart:', error);
    }
  }

  /**
   * Apply discount styling to product card
   */
  applyDiscountToCard(card, discountInfo) {
    if (!discountInfo.hasDiscount) return;

    // Try multiple selectors for price container
    let priceContainer = card.querySelector('.price');
    if (!priceContainer) {
      priceContainer = card.querySelector('[class*="price"]');
    }
    if (!priceContainer) {
      priceContainer = card.querySelector('[id*="price"]');
    }
    if (!priceContainer) {
      // Try to find price in card content
      const cardContent = card.querySelector('.card__content, .card__information, [class*="card"]');
      if (cardContent) {
        priceContainer = cardContent.querySelector('.price, [class*="price"]');
      }
    }
    
    if (!priceContainer) {
      console.warn('Price container not found in card:', card);
      return;
    }

    // Check if discount is already applied
    if (priceContainer.classList.contains('discount-applied')) return;

    // Find price container wrapper
    const priceWrapper = priceContainer.querySelector('.price__container') || priceContainer;
    
    // Check if price__sale div exists (for sale prices)
    let saleDiv = priceWrapper.querySelector('.price__sale');
    const regularDiv = priceWrapper.querySelector('.price__regular');
    
    // Hide regular div
    if (regularDiv) {
      regularDiv.style.display = 'none';
    }
    
    if (!saleDiv) {
      // Create sale div structure if it doesn't exist
      saleDiv = document.createElement('div');
      saleDiv.className = 'price__sale';
      priceWrapper.appendChild(saleDiv);
    }
    
    // Clear existing content
    saleDiv.innerHTML = '';
    
    // Create visually hidden label for regular price
    const regularPriceLabel = document.createElement('span');
    regularPriceLabel.className = 'visually-hidden visually-hidden--inline';
    regularPriceLabel.textContent = 'Regular price';
    
    // Create wrapper for compare at price (old price with strikethrough)
    const compareAtWrapper = document.createElement('span');
    const compareAtPrice = document.createElement('s');
    compareAtPrice.className = 'price-item price-item--regular';
    compareAtPrice.style.cssText = 'color: #7a7a7a; text-decoration: line-through; margin-right: 8px;';
    compareAtPrice.textContent = this.formatMoney(discountInfo.originalPrice);
    compareAtWrapper.appendChild(compareAtPrice);
    
    // Create visually hidden label for sale price
    const salePriceLabel = document.createElement('span');
    salePriceLabel.className = 'visually-hidden visually-hidden--inline';
    salePriceLabel.textContent = 'Sale price';
    
    // Create sale price element (new price)
    const salePriceEl = document.createElement('span');
    salePriceEl.className = 'price-item price-item--sale price-item--last';
    salePriceEl.style.cssText = 'color: #121212; font-weight: 600;';
    salePriceEl.textContent = this.formatMoney(discountInfo.discountedPrice);
    
    // Assemble sale div: regular price label, compare at price, sale price label, sale price
    saleDiv.appendChild(regularPriceLabel);
    saleDiv.appendChild(compareAtWrapper);
    saleDiv.appendChild(salePriceLabel);
    saleDiv.appendChild(salePriceEl);
    
    // Ensure sale div is visible (CSS will handle this with .price--on-sale class)
    // But we need to make sure it's not hidden
    saleDiv.style.display = '';
    
    // Add sale class to price container - this will show .price__sale via CSS
    priceContainer.classList.add('price--on-sale', 'discount-applied');
    
    // Force display if needed (CSS should handle it, but just in case)
    if (window.getComputedStyle(saleDiv).display === 'none') {
      saleDiv.style.display = 'block';
    }
  }

  /**
   * Apply discount styling to product page
   */
  applyDiscountToProductPage(discountInfo) {
    if (!discountInfo.hasDiscount) return;

    const priceContainer = document.querySelector('#price-\\[object Object\\], [id*="price"]');
    if (!priceContainer) return;

    // Check if discount is already applied
    if (priceContainer.classList.contains('discount-applied')) return;

    const priceWrapper = priceContainer.querySelector('.price__container') || priceContainer;
    const regularPriceEl = priceWrapper.querySelector('.price-item--regular:not(s)');
    const salePriceEl = priceWrapper.querySelector('.price-item--sale');
    
    if (regularPriceEl) {
      // Update sale price if exists
      if (salePriceEl) {
        salePriceEl.textContent = this.formatMoney(discountInfo.discountedPrice);
      } else {
        regularPriceEl.textContent = this.formatMoney(discountInfo.discountedPrice);
      }
      
      // Ensure compare at price is shown
      let compareAtEl = priceWrapper.querySelector('s.price-item--regular');
      if (!compareAtEl) {
        compareAtEl = document.createElement('s');
        compareAtEl.className = 'price-item price-item--regular';
        compareAtEl.style.cssText = 'color: #7a7a7a; font-size: inherit; margin-right: 8px; text-decoration: line-through;';
        regularPriceEl.parentNode.insertBefore(compareAtEl, regularPriceEl);
      }
      compareAtEl.textContent = this.formatMoney(discountInfo.originalPrice);
      
      priceContainer.classList.add('price--on-sale', 'discount-applied');
    }
  }

  /**
   * Get variant ID from product card or price element
   */
  getVariantIdFromCard(card) {
    // If card is a price element, find parent card-wrapper
    let searchElement = card;
    if (card.classList && card.classList.contains('price')) {
      searchElement = card.closest('.card-wrapper, .product-card-wrapper, .card') || card;
    }
    
    // If we have card-information, find the card-wrapper parent
    if (searchElement.classList && searchElement.classList.contains('card-information')) {
      searchElement = searchElement.closest('.card-wrapper, .product-card-wrapper, .card') || searchElement;
    }
    
    // Search in the entire card tree starting from card-wrapper
    // First, try to find all inputs in the entire card
    const allInputs = searchElement.querySelectorAll && searchElement.querySelectorAll('input[name="id"], input.product-variant-id');
    if (allInputs && allInputs.length > 0) {
      for (let input of allInputs) {
        if (input.value && input.value.trim() !== '' && !input.disabled) {
          return input.value;
        }
      }
    }
    
    // Try to find in forms
    const forms = searchElement.querySelectorAll && searchElement.querySelectorAll('form');
    if (forms && forms.length > 0) {
      for (let form of forms) {
        const formInput = form.querySelector('input[name="id"][value], input.product-variant-id[value]');
        if (formInput && formInput.value && !formInput.disabled) {
          return formInput.value;
        }
      }
    }
    
    // Try data attributes
    if (searchElement.dataset && searchElement.dataset.variantId) {
      return searchElement.dataset.variantId;
    }
    
    // Try to find from product link and search in card-wrapper
    const productLink = searchElement.querySelector && searchElement.querySelector('a[href*="/products/"]');
    if (productLink) {
      const href = productLink.getAttribute('href');
      const productMatch = href.match(/\/products\/([^\/\?#]+)/);
      if (productMatch) {
        // Find the card-wrapper that contains this link
        const productCard = productLink.closest('.card-wrapper, .product-card-wrapper, .card');
        if (productCard) {
          // Search all inputs in the card
          const cardInputs = productCard.querySelectorAll('input[name="id"], input.product-variant-id');
          for (let input of cardInputs) {
            if (input.value && input.value.trim() !== '' && !input.disabled) {
              return input.value;
            }
          }
        }
      }
    }
    
    return null;
  }

  /**
   * Get selected variant ID on product page
   */
  getSelectedVariantId() {
    const variantInput = document.querySelector('input[name="id"][value]');
    if (variantInput && variantInput.value) return variantInput.value;
    
    const variantSelect = document.querySelector('select[name="id"]');
    if (variantSelect && variantSelect.value) return variantSelect.value;
    
    // Try product-info element
    const productInfo = document.querySelector('product-info');
    if (productInfo && productInfo.dataset.selectedVariantId) {
      return productInfo.dataset.selectedVariantId;
    }
    
    return null;
  }

  /**
   * Format money value using Shopify's formatMoney if available
   */
  formatMoney(cents) {
    if (typeof window.Shopify !== 'undefined' && window.Shopify.formatMoney) {
      return window.Shopify.formatMoney(cents);
    }
    // Fallback formatting
    return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(cents / 100);
  }
}

// Initialize on page load
if (typeof window !== 'undefined') {
  window.discountDisplay = new DiscountDisplay();
}

